import { useState, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient, ApiError } from '../lib/api';
import { useAuth } from '../store/auth';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import {
  Activity, Search, Bot, Zap, AlertTriangle,
  Smartphone, Monitor, Tablet, HelpCircle,
} from 'lucide-react';
import { formatRelativeTime, formatCurrency, getDeviceLabel } from '../lib/utils';
import { CountryFlag } from '../components/ui/CountryFlag';
import { SubIdLightning } from '../components/SubIdLightning';
import { useOfferLookup, useCampaignLookup } from '../hooks/useLookups';
import { usePollMultiplier } from '../hooks/usePollMultiplier';

// Shape returned by /api/v1/clicks/live (raw row from Analytics Engine + D1 conversion enrichment)
interface LiveClick {
  timestamp: string;
  click_id: string;
  sub_id: string;
  campaign_slug: string;
  offer_id_str: string;
  country: string;
  isp: string;
  device: string;
  os: string;
  source: string;
  ip: string;
  user_agent: string;
  destination_url: string;
  hostname: string;
  fraud_flags: string;
  is_unique: number;
  is_bot: number;
  bot_score: number;
  is_datacenter: number;
  campaign_id: number;
  offer_id: number;
  conversion: { revenue: number; payout: number; status: string } | null;
}

function DeviceIcon({ device }: { device: string | null | undefined }) {
  const d = (device ?? '').toLowerCase();
  if (d.includes('bot')) return <Bot size={12} className="inline-block text-neon-orange" />;
  if (d.includes('mobile')) return <Smartphone size={12} className="inline-block text-neon-cyan" />;
  if (d.includes('tablet')) return <Tablet size={12} className="inline-block text-neon-purple" />;
  if (d.includes('desktop')) return <Monitor size={12} className="inline-block text-neon-green" />;
  return <HelpCircle size={12} className="inline-block text-text-tertiary" />;
}

function SkeletonRow() {
  return (
    <tr className="animate-pulse">
      {Array.from({ length: 10 }).map((_, i) => (
        <td key={i}><div className="h-3 bg-bg-tertiary rounded w-4/5" /></td>
      ))}
    </tr>
  );
}

// Just two sizes. 50 = scan glance, 100 = expanded view. Bigger sizes
// invited "load 1000 just to scroll through" pattern that wasted bandwidth.
// Search filter handles drill-down beyond top 100.
const PAGE_SIZES = [50, 100] as const;
type PageSize = (typeof PAGE_SIZES)[number];

function normalizeIp(ip: string | null | undefined) {
  return (ip ?? '').trim().toLowerCase();
}

function isBotClick(row: LiveClick) {
  return Number(row.is_bot) === 1;
}

export function ClicksPage() {
  // User feedback: yesterday range was redundant — Statistics page already
  // covers historical breakdowns. Clicks page is now ALWAYS the live "today"
  // realtime stream. Refresh button also removed — polling cadence is fast
  // enough that manual refresh added no value.
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState<'all' | 'quality' | 'bots'>('all');
  const [pageSize, setPageSize] = useState<PageSize>(50);
  const offerLookup = useOfferLookup();
  const campaignLookup = useCampaignLookup();
  const pollMult = usePollMultiplier();

  // NOTE: reverted auto-scoping per user request. Clicks is a network-wide
  // view for both roles. IP column for user role is redacted at row level
  // by backend (PII protection for other publishers' clicks) — user
  // accepted this trade-off.

  const list = useQuery({
    queryKey: ['clicks-live', filterMode, pageSize],
    queryFn: () =>
      apiClient.get<LiveClick[]>('/api/v1/clicks/live', {
        since_days: 1,
        only_bots: filterMode === 'bots' ? 1 : undefined,
        exclude_bots: filterMode === 'quality' ? 1 : undefined,
        limit: pageSize,
      }),
    refetchInterval: 15_000 * pollMult,
    retry: false,
  });

  // ─── Realtime DO stream (today only, always-on layer) ────────────────────
  // Brought back after user feedback: AE alone has 30-60s ingestion lag,
  // making the today view feel stale. DO holds the last 500 clicks per
  // hostname-shard with sub-2s tracker→DO write latency. We poll it at 5s
  // and merge into the AE results — DO entries take priority for click_id
  // overlap (they're newer + more accurate). For yesterday range, DO
  // doesn't help (all data ≥24h old, beyond DO buffer) so we skip it.
  //
  // OPTIMIZATION (delta-fetch pattern):
  //   - limit reduced 500 → 100. DO only needs to fill the ~60s AE-lag gap;
  //     at typical traffic rates that's tens of clicks, not hundreds. Cuts
  //     payload size ~80% per poll (was ~250KB, now ~50KB).
  //   - `since` param: pass last-seen timestamp so DO returns only NEW
  //     clicks since last poll. With 5s cadence, payload usually <10 rows.
  //     Falls back to full fetch on first load (since=0).
  //   - Accumulator ref keeps the previous merged result so partial fetches
  //     still render the full visible buffer.
  const lastRealtimeTsRef = useRef<number>(0);
  const realtimeAccumRef = useRef<LiveClick[]>([]);

  const realtime = useQuery({
    queryKey: ['clicks-realtime', pageSize],
    queryFn: async () => {
      const delta = await apiClient.get<LiveClick[]>('/api/v1/clicks/realtime', {
        limit: pageSize, // match what the table actually shows
        since: lastRealtimeTsRef.current,
      });
      // Update high-water mark for next poll (timestamp is ISO string "YYYY-MM-DD HH:MM:SS")
      for (const cl of delta) {
        const t = new Date((cl.timestamp || '').replace(' ', 'T') + 'Z').getTime();
        if (Number.isFinite(t) && t > lastRealtimeTsRef.current) {
          lastRealtimeTsRef.current = t;
        }
      }
      // Merge into accumulator, dedup by click_id, keep newest pageSize rows
      const seen = new Set<string>();
      const merged: LiveClick[] = [];
      for (const r of [...delta, ...realtimeAccumRef.current]) {
        if (!r.click_id || seen.has(r.click_id)) continue;
        seen.add(r.click_id);
        merged.push(r);
      }
      merged.sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));
      realtimeAccumRef.current = merged.slice(0, pageSize);
      return realtimeAccumRef.current;
    },
    refetchInterval: 10_000 * pollMult, // user feedback: 5s → 10s, less noisy + half the requests
    retry: false,
    placeholderData: (prev) => prev, // keep showing previous data during refetch
  });

  // Fallback to D1 conversions if Analytics Engine not configured
  const aeError = list.error as ApiError | undefined;
  const aeNotConfigured = aeError?.code === 'AE_NOT_CONFIGURED';
  // 429 from Cloudflare AE SQL API hits when many users concurrently poll.
  // The server backs off + retries internally — by the next 15s poll, things
  // are usually fine. So we DON'T surface the scary "Error fetching clicks"
  // banner for transient rate limits; we just keep showing whatever data
  // was last loaded (placeholderData in TanStack Query handles that).
  const aeRateLimited = aeError?.code === 'AE_RATE_LIMITED';

  // Merge AE (historical, ~30-60s lag) with DO realtime stream (sub-2s lag
  // but limited to last ~500 clicks per shard). DO entries take precedence
  // for click_id overlap. Server-side bot filter only applies to AE result;
  // re-apply on the client side after merge so DO entries also honor the
  // current `filterMode` toggle.
  const merged = useMemo(() => {
    const aeRows = list.data ?? [];
    const rtRows = realtime.data ?? [];
    if (rtRows.length === 0) {
      return [...aeRows].sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));
    }
    const seen = new Set<string>();
    const out: LiveClick[] = [];
    // RT first — newer takes precedence over AE for the same click_id
    for (const r of rtRows) {
      if (seen.has(r.click_id)) continue;
      seen.add(r.click_id);
      out.push(r);
    }
    for (const r of aeRows) {
      if (seen.has(r.click_id)) continue;
      seen.add(r.click_id);
      out.push(r);
    }
    let filtered = out;
    if (filterMode === 'bots') filtered = filtered.filter(isBotClick);
    else if (filterMode === 'quality') filtered = filtered.filter((r) => !isBotClick(r));
    filtered.sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));
    return filtered.slice(0, pageSize);
  }, [list.data, realtime.data, filterMode, pageSize]);

  // Search across all visible columns. We resolve campaign/offer names through
  // the same lookup hooks the table cells use, so what user types matches what
  // they see on screen (not the raw slug/id).
  const filtered = useMemo(() => {
    const rows = merged;
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      const campaignName = campaignLookup.nameFromSlug(r.campaign_slug);
      const offerName = offerLookup.getName(r.offer_id || null);
      return (
        (r.sub_id ?? '').toLowerCase().includes(q) ||
        (r.click_id ?? '').toLowerCase().includes(q) ||
        (r.country ?? '').toLowerCase().includes(q) ||
        (r.campaign_slug ?? '').toLowerCase().includes(q) ||
        campaignName.toLowerCase().includes(q) ||
        offerName.toLowerCase().includes(q) ||
        (r.source ?? '').toLowerCase().includes(q) ||
        (r.isp ?? '').toLowerCase().includes(q) ||
        (r.ip ?? '').toLowerCase().includes(q)
      );
    });
  }, [merged, search, campaignLookup, offerLookup]);

  const duplicateIpCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of merged) {
      const ip = normalizeIp(row.ip);
      if (!ip) continue;
      counts.set(ip, (counts.get(ip) ?? 0) + 1);
    }
    return counts;
  }, [merged]);

  // D4: Counts computed from `merged` (RT + AE) so pills agree with the table row count.
  // Previously used list.data (AE only) which diverged from the realtime-enriched view.
  const counts = useMemo(() => {
    const all = merged.length;
    const bots = merged.filter(isBotClick).length;
    return { all, bots, quality: all - bots };
  }, [merged]);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold uppercase tracking-widest flex items-center gap-2">
            <Zap size={20} className="text-neon-cyan" />
            Clicks
          </h1>
          {/* Decluttered: removed the "// N records" subtitle and the verbose
              LIVE/yesterday explainer paragraph. The same record-count info is
              still rendered in the table footer (`showing X of Y records`),
              and the `today/yesterday` range buttons themselves communicate
              which data layer is active. Per user request to hide noise. */}
          {aeNotConfigured && (
            <p className="text-sm text-text-secondary font-mono">// analytics engine not configured</p>
          )}
        </div>
        {/* Range/refresh controls removed: Clicks page is always today realtime;
            yesterday historical breakdown lives on Statistics. */}
      </div>

      {aeNotConfigured ? (
        <div className="flex items-start gap-3 px-4 py-3 border border-neon-yellow/40 bg-neon-yellow/5 rounded-sm">
          <AlertTriangle size={16} className="text-neon-yellow mt-0.5 shrink-0" />
          <div className="text-xs font-mono text-neon-yellow leading-relaxed">
            {/* Admin-jargon (binding names) shown only to admins; publishers
                get a friendly message instead. */}
            {user?.role === 'admin' ? (
              <>
                <strong>Analytics Engine SQL not configured.</strong> Set
                {' '}<code className="text-neon-cyan">CLOUDFLARE_ACCOUNT_ID</code> and
                {' '}<code className="text-neon-cyan">CLOUDFLARE_ANALYTICS_TOKEN</code> on the API worker.
              </>
            ) : (
              <>
                <strong>Click stream sedang tidak tersedia.</strong> Data klik realtime
                sementara tidak bisa diakses. Coba lagi beberapa menit lagi atau hubungi admin.
              </>
            )}
          </div>
        </div>
      ) : aeRateLimited ? (
        // Show a small "throttled" hint instead of the scary error banner.
        // Next poll will likely succeed (server retried + backed off internally).
        <div className="flex items-center gap-2 px-3 py-1.5 border border-neon-yellow/30 bg-neon-yellow/5 rounded-sm">
          <span className="w-1.5 h-1.5 rounded-full bg-neon-yellow animate-pulse" />
          <p className="text-[10px] font-mono text-neon-yellow/80">
            Analytics throttled — retrying in {Math.round((15_000 * pollMult) / 1000)}s
          </p>
        </div>
      ) : list.error ? (
        <div className="flex items-start gap-3 px-4 py-3 border border-neon-red/40 bg-neon-red/5 rounded-sm">
          <AlertTriangle size={16} className="text-neon-red mt-0.5 shrink-0" />
          <p className="text-xs font-mono text-neon-red">
            Error fetching clicks: {(list.error as Error).message}
          </p>
        </div>
      ) : null}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="flex-1 max-w-xs">
          <Input
            placeholder="Search Sub ID / Campaign / Offer / Country / IP / ISP..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            icon={<Search size={14} />}
          />
        </div>
        <div className="inline-flex bg-bg-secondary border border-border-subtle rounded-sm p-1">
          <button
            onClick={() => setFilterMode('all')}
            className={`px-3 py-1.5 text-xs font-display uppercase tracking-wider rounded-sm ${
              filterMode === 'all' ? 'bg-neon-cyan/10 text-neon-cyan' : 'text-text-secondary'
            }`}
          >
            All ({counts.all})
          </button>
          <button
            onClick={() => setFilterMode('quality')}
            className={`px-3 py-1.5 text-xs font-display uppercase tracking-wider rounded-sm ${
              filterMode === 'quality' ? 'bg-neon-green/10 text-neon-green' : 'text-text-secondary'
            }`}
          >
            Quality ({counts.quality})
          </button>
          <button
            onClick={() => setFilterMode('bots')}
            className={`px-3 py-1.5 text-xs font-display uppercase tracking-wider rounded-sm ${
              filterMode === 'bots' ? 'bg-neon-orange/10 text-neon-orange' : 'text-text-secondary'
            }`}
          >
            Bots ({counts.bots})
          </button>
        </div>
        {/* Page size selector — server caps at 1000 */}
        <div className="inline-flex bg-bg-secondary border border-border-subtle rounded-sm p-1">
          <span className="px-2 py-1.5 text-[10px] font-display uppercase tracking-wider text-text-tertiary">
            Show
          </span>
          {PAGE_SIZES.map((s) => (
            <button
              key={s}
              onClick={() => setPageSize(s)}
              className={`px-2 py-1.5 text-xs font-display uppercase tracking-wider rounded-sm transition-all ${
                pageSize === s ? 'bg-neon-magenta/10 text-neon-magenta' : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <Card className="p-0">
        <div className="px-5 py-4 border-b border-border-subtle">
          <h3 className="font-display text-sm uppercase tracking-wider text-neon-cyan flex items-center gap-2">
            <Activity size={14} />
            Click Stream
            {list.isRefetching && (
              <span className="ml-2 text-[10px] text-text-tertiary font-mono">syncing...</span>
            )}
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="table-cyber">
            <thead>
              <tr>
                <th>Time</th>
                <th>Sub ID</th>
                <th>Campaign</th>
                <th>Offer</th>
                <th>Country</th>
                <th>Device</th>
                <th>Source</th>
                <th>ISP</th>
                <th>IP</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {list.isLoading && Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)}

              {!list.isLoading && filtered.map((row) => {
                const isBot = isBotClick(row);
                const duplicateIpCount = duplicateIpCounts.get(normalizeIp(row.ip)) ?? 0;
                const hasDuplicateIp = duplicateIpCount > 1;
                return (
                  <tr key={row.click_id}>
                    <td className="font-mono text-xs text-text-secondary whitespace-nowrap"
                        title={row.timestamp}>
                      {/* D5: Realtime DO may already send ISO format with 'Z'; only
                           append 'Z' when the string lacks a timezone designator. */}
                      {formatRelativeTime(new Date(
                        /[TZ+]/.test(row.timestamp)
                          ? row.timestamp
                          : row.timestamp.replace(' ', 'T') + 'Z'
                      ).getTime())}
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs">
                          {row.sub_id
                            ? <SubIdLightning subId={row.sub_id}>{row.sub_id}</SubIdLightning>
                            : <span className="text-text-tertiary">—</span>}
                        </span>
                        {row.conversion && (
                          <span
                            className={`inline-flex items-center font-mono text-[10px] px-1.5 py-0.5 rounded border ${
                              row.conversion.status === 'reversed' || row.conversion.status === 'rejected'
                                ? 'border-neon-red/40 text-neon-red bg-neon-red/5 line-through'
                                : 'border-neon-yellow/40 text-neon-yellow bg-neon-yellow/5'
                            }`}
                            title={`Conversion: ${row.conversion.status} · Payout ${formatCurrency(row.conversion.payout)}`}
                          >
                            {formatCurrency(row.conversion.revenue)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td
                      className="font-mono text-xs text-neon-cyan whitespace-nowrap"
                      title={row.campaign_slug ? `Slug: ${row.campaign_slug}` : undefined}
                    >
                      {campaignLookup.nameFromSlug(row.campaign_slug)}
                    </td>
                    <td className="font-mono text-xs text-text-secondary" title={`Offer ID: ${row.offer_id || 'unlinked'}`}>
                      {offerLookup.getName(row.offer_id || null)}
                    </td>
                    <td>
                      {row.country ? <CountryFlag iso2={row.country} showCode size={20} /> : '—'}
                    </td>
                    <td>
                      {row.device ? (
                        <span className="flex items-center gap-1.5 font-mono text-xs">
                          <DeviceIcon device={row.device} />
                          <span className="capitalize">
                            {getDeviceLabel(row.device)}
                            {row.os && row.os !== 'other' ? ` · ${row.os}` : ''}
                          </span>
                        </span>
                      ) : '—'}
                    </td>
                    <td className="font-mono text-xs">{row.source || '—'}</td>
                    <td
                      className="font-mono text-[10px] text-text-secondary truncate max-w-[140px]"
                      title={row.isp || undefined}
                    >
                      {row.isp || <span className="text-text-tertiary">—</span>}
                    </td>
                    <td className="font-mono text-[10px] text-text-tertiary">{row.ip?.slice(0, 15)}{row.ip?.length > 15 ? '…' : ''}</td>
                    <td>
                      <div
                        className="flex items-center gap-1.5 flex-wrap"
                        title={hasDuplicateIp ? `IP appears ${duplicateIpCount} times in the loaded click buffer` : undefined}
                      >
                        {hasDuplicateIp && (
                          <Badge variant="error">
                            <AlertTriangle size={10} /> DUP IP x{duplicateIpCount}
                          </Badge>
                        )}
                        {isBot ? (
                          <Badge variant="error">
                            <Bot size={10} /> {Math.round(row.bot_score)}
                          </Badge>
                        ) : !hasDuplicateIp ? (
                          <Badge variant="info" pulse>OK</Badge>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}

              {!list.isLoading && !list.error && filtered.length === 0 && (
                <tr>
                  <td colSpan={10} className="text-center py-14 text-text-tertiary">
                    <Activity className="mx-auto mb-3 opacity-30" size={36} />
                    <p className="font-mono text-xs">
                      {search ? 'no results matching your filter' : 'no clicks in this time range — fire some traffic'}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {filtered.length > 0 && (
          <div className="px-5 py-3 border-t border-border-subtle text-[10px] font-mono text-text-tertiary flex items-center justify-between gap-3 flex-wrap">
            <span>
              showing {filtered.length} of {list.data?.length ?? 0} records
              {search && ` · filter: "${search}"`}
              {filterMode !== 'all' && ` · ${filterMode} only`}
            </span>
            {/* "Load more" hops between the two available sizes (50 ↔ 100).
                When at max (100), suggest using search to drill down further
                rather than offering ever-larger payloads — matches the
                payload-cap design decision (search > paging). */}
            {(list.data?.length ?? 0) >= pageSize && pageSize === 50 && (
              <button
                onClick={() => setPageSize(100)}
                className="text-neon-cyan hover:opacity-70 underline-offset-2 hover:underline"
              >
                Load more (50 → 100) ↓
              </button>
            )}
            {(list.data?.length ?? 0) >= pageSize && pageSize === 100 && (
              <span className="text-neon-yellow">⚠ showing top 100 — use search to find specific clicks</span>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
